import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Avatar,
  Badge,
  useMediaQuery,
  Chip,
} from '@mui/material';
import {
  appBarSx,
  toolbarSx,
} from '../../theme/recipes/navigation';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import { useNavigate } from 'react-router-dom';
import NotificationsModal from '../modals/notifications/NotificationsModal';
import { isAdminContext } from '../../utils/access';

// Importar la nueva capa intermedia
import {
  useNotificationManager,
} from '../../hooks';

const Header: React.FC = () => {
  const isMobile = useMediaQuery('(max-width:900px)');
  const navigate = useNavigate();
  const { toggleSidebar } = useUIStore();
  const {
    user,
    getSessionTimeRemaining,
    checkSessionExpiration,
    lastActivityTime,
    isSpecialtyActive,
  } = useAuthStore();

  const { unreadCount } = useNotificationManager();

  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(0);
  // Suscripción deshabilitada temporalmente: acceso completo para todos
  const hasActiveSubscription = true;

  // Actualizar tiempo de sesión
  useEffect(() => {
    const updateSessionInfo = () => {
      const expired = checkSessionExpiration();
      if (!expired) {
        const timeRemaining = getSessionTimeRemaining();
        setSessionTimeRemaining(timeRemaining);
      }
    };

    // Verificar inmediatamente
    updateSessionInfo();

    // Actualizar cada minuto
    const interval = setInterval(updateSessionInfo, 60000);
    return () => clearInterval(interval);
  }, [checkSessionExpiration, getSessionTimeRemaining]);

  // Refrescar de inmediato el chip cuando hay actividad del usuario.
  useEffect(() => {
    const expired = checkSessionExpiration();
    if (!expired) {
      setSessionTimeRemaining(getSessionTimeRemaining());
    }
  }, [lastActivityTime, checkSessionExpiration, getSessionTimeRemaining]);

  const handleNotifications = () => {
    setNotificationsOpen(true);
  };

  const getSessionColor = (timeRemaining: number) => {
    if (timeRemaining <= 2) return 'error';
    if (timeRemaining <= 5) return 'warning';
    return 'success';
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={appBarSx}
    >
      <Toolbar sx={toolbarSx}>
        {/* Left Section */}
        <Box
          sx={{ display: 'flex', alignItems: 'center', flex: 1, maxWidth: 600 }}
        >
          {/* Menu Button - Mobile */}
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleSidebar}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Sidebar Toggle Button - Desktop */}
          <IconButton
            edge="start"
            color="inherit"
            aria-label="toggle sidebar"
            onClick={toggleSidebar}
            sx={{ mr: 2, display: { xs: 'none', md: 'flex' } }}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        {/* Right Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
          {/* Session Timer */}
          {sessionTimeRemaining > 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Chip
                icon={<AccessTimeIcon />}
                label={`${sessionTimeRemaining}m`}
                color={getSessionColor(sessionTimeRemaining) as any}
                size="small"
                variant="outlined"
                sx={{ mr: 1, fontSize: '0.75rem' }}
              />
            </motion.div>
          )}

          {/* Notifications - Solo para doctores con especialidad activa y suscripción activa */}
          {!isAdminContext(user) &&
            isSpecialtyActive &&
            hasActiveSubscription && (
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.1 }}
              >
                <IconButton
                  color="inherit"
                  onClick={handleNotifications}
                  sx={{ mr: 1 }}
                >
                  <Badge badgeContent={unreadCount} color="error">
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
              </motion.div>
            )}

          {/* User Avatar */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.1 }}
          >
            <IconButton
              sx={{ p: 0, ml: 1 }}
              aria-label="account"
              onClick={() => navigate('/profile')}
            >
              <Avatar
                src={user?.avatar || undefined}
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: 'primary.main',
                  fontSize: '1rem',
                  fontWeight: 600,
                }}
              >
                {user?.name?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
          </motion.div>


        </Box>
      </Toolbar>

      {/* Notifications Modal */}
      <NotificationsModal
        open={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
      />
    </AppBar>
  );
};

export default Header;
